import { identity } from 'lodash'

import {
  ADD_PLACE,
  ADD_PLACE_WITH_VALUES,
  ATTACH_BOOK_TO_PLACE,
  ATTACH_TAG_TO_PLACE,
  DELETE_PLACE,
  DUPLICATE_PLACE,
  EDIT_PLACE,
  EDIT_PLACE_CUSTOM_ATTRIBUTE,
  EDIT_PLACE_DESCRIPTION,
  EDIT_PLACE_NAME,
  EDIT_PLACE_NOTES,
  EDIT_PLACE_TEMPLATE_ATTRIBUTE,
  LOAD_PLACES,
  LOAD_PLACE,
  BATCH_LOAD_PLACE,
  REMOVE_PLACE,
  REMOVE_BOOK_FROM_PLACE,
  REMOVE_TAG_FROM_PLACE,
  REORDER_PLACE_MANUALLY,
} from '../constants/ActionTypes'
import { place } from '../store/initialState'
import selectors from '../selectors'

/**
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../index').SlateSelection} SlateSelection
 * @typedef {import('../index').TextAttributeData} TextAttributeData
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 */

/**
 * @typedef {ReturnType<typeof addPlace> | ReturnType<typeof addPlaceWithValues> | ReturnType<typeof editPlace> | ReturnType<typeof duplicatePlace> | ReturnType<typeof editPlaceTemplateAttribute> | ReturnType<typeof deletePlace> | ReturnType<typeof addTag> | ReturnType<typeof addBook> | ReturnType<typeof removeTag> | ReturnType<typeof removeBook> | ReturnType<typeof editPlaceName> | ReturnType<typeof editPlaceDescription> | ReturnType<typeof editPlaceNotes> | ReturnType<typeof editPlaceCustomAttribute> | ReturnType<typeof load> | ReturnType<typeof loadSingle> | ReturnType<typeof batchLoad> | ReturnType<typeof removeSingle> | ReturnType<typeof _reorderPlaceManually>} PlacesActions
 */

const { visibleSortedPlacesByCategorySelector } = selectors(identity)

/**
 * @return {{ type: 'ADD_PLACE', name: String, description: String, notes: Slate }}
 */
export function addPlace() {
  return { type: ADD_PLACE, name: place.name, description: place.description, notes: place.notes }
}

/**
 * @param {Partial<Place>} place
 * @return {{ type: 'ADD_PLACE_WITH_VALUES', place: Partial<Place> }}
 */
export function addPlaceWithValues(place) {
  return { type: ADD_PLACE_WITH_VALUES, place }
}

/**
 * @typedef PlaceUpdateValue
 * @property {Place[keyof Place]} value
 * @property {DOMSelection | SlateSelection} [selection]
 */

/**
 * @typedef {Record<keyof Place, PlaceUpdateValue>} UpdatePlaceAttributes
 */

/**
 * @param {number} id
 * @param {Partial<UpdatePlaceAttributes>} attributes
 * @return {{ type: 'EDIT_PLACE', id: number, attributes: Partial<UpdatePlaceAttributes> }}
 */
export function editPlace(id, attributes) {
  return { type: EDIT_PLACE, id, attributes }
}

/**
 * @param {number} id
 * @return {{ type: 'DUPLICATE_PLACE', id: number }}
 */
export function duplicatePlace(id) {
  return { type: DUPLICATE_PLACE, id }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_PLACE_TEMPLATE_ATTRIBUTE', id: number, templateId: String, name: String, value: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editPlaceTemplateAttribute(id, templateId, name, value, selection) {
  return {
    type: EDIT_PLACE_TEMPLATE_ATTRIBUTE,
    id,
    templateId,
    name,
    value,
    selection,
  }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_PLACE', id: number }}
 */
export function deletePlace(id) {
  return { type: DELETE_PLACE, id }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'ATTACH_TAG_TO_PLACE', id: number, tagId: number }}
 */
export function addTag(id, tagId) {
  return { type: ATTACH_TAG_TO_PLACE, id, tagId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'ATTACH_BOOK_TO_PLACE', id: number, bookId: number | 'series' }}
 */
export function addBook(id, bookId) {
  return { type: ATTACH_BOOK_TO_PLACE, id, bookId }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'REMOVE_TAG_FROM_PLACE', id: number, tagId: number }}
 */
export function removeTag(id, tagId) {
  return { type: REMOVE_TAG_FROM_PLACE, id, tagId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'REMOVE_BOOK_FROM_PLACE', id: number, bookId: number | 'series' }}
 */
export function removeBook(id, bookId) {
  return { type: REMOVE_BOOK_FROM_PLACE, id, bookId }
}

/**
 * @param {number} id
 * @param {String} newName
 * @param {DOMSelection | Null} selection
 * @return {{ type: 'EDIT_PLACE_NAME', id: number, newName: String, selection: DOMSelection | Null }}
 */
export function editPlaceName(id, newName, selection) {
  return { type: EDIT_PLACE_NAME, id, newName, selection }
}

/**
 * @param {number} id
 * @param {String} newDescription
 * @param {DOMSelection} selection
 * @return {{ type: 'EDIT_PLACE_DESCRIPTION', id: number, newDescription: String, selection: DOMSelection }}
 */
export function editPlaceDescription(id, newDescription, selection) {
  return { type: EDIT_PLACE_DESCRIPTION, id, newDescription, selection }
}

/**
 * @param {number} id
 * @param {Slate} newNotes
 * @param {SlateSelection | Null} selection
 * @return {{ type: 'EDIT_PLACE_NOTES', id: number, newNotes: Slate, selection: SlateSelection | Null }}
 */
export function editPlaceNotes(id, newNotes, selection) {
  return { type: EDIT_PLACE_NOTES, id, newNotes, selection }
}

/**
 * @param {number} id
 * @param {String} name
 * @param {TextAttributeData} newValue
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_PLACE_CUSTOM_ATTRIBUTE', id: number, name: String, newValue: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editPlaceCustomAttribute(id, name, newValue, selection) {
  return { type: EDIT_PLACE_CUSTOM_ATTRIBUTE, id, name, newValue, selection }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Place>} places
 * @return {{ type: 'LOAD_PLACES', patching: boolean, places: Array<Place> }}
 */
export function load(patching, places) {
  return { type: LOAD_PLACES, patching, places }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Place} place
 * @return {{ type: 'LOAD_PLACE', patching: boolean, place: Place }}
 */
export function loadSingle(patching, place) {
  return { type: LOAD_PLACE, patching, place }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Place>} places
 * @return {{ type: 'BATCH_LOAD_PLACE', patching: boolean, places: Array<Place> }}
 */
export function batchLoad(patching, places) {
  return { type: BATCH_LOAD_PLACE, patching, places }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Place} place
 * @return {{ type: 'REMOVE_PLACE', patching: boolean, place: Place }}
 */
export function removeSingle(patching, place) {
  return { type: REMOVE_PLACE, patching, place }
}

/**
 * @param {number} id
 * @param {number} oldPosition
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @param {Record<String, Array<Place>>} placesByCategory
 * @return {{ type: 'REORDER_PLACE_MANUALLY', id: number, oldPosition: number, newPosition: number, newCategoryId: String | Null, direction: 'up' | 'down', placesByCategory: Record<String, Array<Place>> }}
 */
export const _reorderPlaceManually = (
  id,
  oldPosition,
  newPosition,
  newCategoryId,
  direction,
  placesByCategory
) => {
  return {
    type: REORDER_PLACE_MANUALLY,
    id,
    oldPosition,
    newPosition,
    newCategoryId,
    direction,
    placesByCategory,
  }
}

/**
 * @param {number} placeId
 * @param {number} oldPosition
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @return {function(Dispatch, GetState): Void}
 */
export const reorderPlaces =
  (placeId, oldPosition, newPosition, newCategoryId, direction) => (dispatch, getState) => {
    const visiblePlacesByCategory = visibleSortedPlacesByCategorySelector(getState())
    dispatch(
      _reorderPlaceManually(
        placeId,
        oldPosition,
        newPosition,
        newCategoryId,
        direction,
        visiblePlacesByCategory
      )
    )
  }
